/*!
 * Infinite Scroll pour les collections Shopify
 * Chargement automatique des produits au scroll
 */

class InfiniteScroll {
  constructor(options = {}) {
    // Configuration par défaut
    this.config = {
      triggerMargin: '200px 0px',
      threshold: 0.1,
      animationDelay: 100,
      triggerOffset: 200,
      mobileOffset: 100,
      ...options
    };

    this.container = document.querySelector('[data-products-grid]');
    this.grid = document.querySelector('#CollectionLoop');
    this.trigger = null;
    this.loader = document.querySelector('.grid__loader');
    this.pagination = document.querySelector('.pagination-custom');
    this.isLoading = false;
    this.hasNextPage = false;
    this.nextPageUrl = null;
    this.lastLoadedUrl = null;
    
    this.init();
  }

  init() {
    if (!this.container || !this.grid) return;
    document.documentElement.classList.add('infinite-scroll-enabled');
    // Créer aria-live si absent
    if (!document.querySelector('[data-infinite-live]')) {
      const live = document.createElement('div');
      live.setAttribute('data-infinite-live','');
      live.setAttribute('aria-live','polite');
      live.setAttribute('aria-atomic','true');
      live.style.position='absolute';
      live.style.width='1px';
      live.style.height='1px';
      live.style.overflow='hidden';
      live.style.clip='rect(1px,1px,1px,1px)';
      live.style.whiteSpace='nowrap';
      document.body.appendChild(live);
    }
    this.checkPagination();
    if (this.hasNextPage) {
      this.createTrigger();
      this.setupObserver();
      this.hidePagination();
    }
  }

  checkPagination() {
    const nextLink = document.querySelector('.pagination-custom__next[href]');
    if (nextLink && !nextLink.classList.contains('is-hidden')) {
      this.hasNextPage = true;
      this.nextPageUrl = nextLink.getAttribute('href');
    } else {
      // Fallback: tenter de déduire l'URL de la page suivante depuis la query string page=
      const url = new URL(window.location.href);
      const currentPage = parseInt(url.searchParams.get('page') || '1', 10);
      // On suppose qu'il existe potentiellement une page suivante tant qu'on n'a pas tenté un chargement
      // On ne met pas hasNextPage à true ici sans lien explicite pour éviter une boucle; simplement note.
    }
  }

  createTrigger() {
    // Créer l'élément déclencheur invisible INSIDE la grille pour détecter la fin des produits
    this.trigger = document.createElement('div');
    this.trigger.className = 'infinite-scroll-trigger';
    this.trigger.setAttribute('data-infinite-scroll-trigger', '');
    const isMobile = window.innerWidth <= 749;
    const offset = isMobile ? this.config.mobileOffset : this.config.triggerOffset;
    this.trigger.style.cssText = `height:1px;width:100%;position:relative;bottom:${offset}px;`;
    // L'ajouter en bas de la grille
    if (this.grid) {
      this.grid.appendChild(this.trigger);
    } else {
      this.container.appendChild(this.trigger);
    }
  }

  setupObserver() {
    const options = {
      root: null,
      rootMargin: this.config.triggerMargin,
      threshold: this.config.threshold
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasNextPage && !this.isLoading) {
          this.loadNextPage();
        }
      });
    }, options);

    this.observer.observe(this.trigger);
  }

  hidePagination() {
    if (this.pagination) {
      this.pagination.style.display = 'none';
    }
  }

  showLoader() {
    if (this.loader) {
      this.loader.classList.add('is-loading');
    }
    const live = document.querySelector('[data-infinite-live]');
    if (live) live.textContent = 'Chargement des produits...';
  }

  hideLoader() {
    if (this.loader) {
      this.loader.classList.remove('is-loading');
    }
  }

  async loadNextPage() {
    if (!this.nextPageUrl || this.isLoading) return;
    if (this.lastLoadedUrl && this.lastLoadedUrl === this.nextPageUrl) return; // éviter doublon
    this.lastLoadedUrl = this.nextPageUrl;
    this.isLoading = true;
    this.showLoader();
    try {
      const response = await fetch(this.nextPageUrl, { credentials: 'same-origin', headers: { 'X-Requested-With':'XMLHttpRequest' }});
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newProducts = doc.querySelectorAll('#CollectionLoop [data-grid-item]');
      const currentProductCount = this.grid.querySelectorAll('[data-grid-item]').length;
      if (newProducts.length > 0) {
        const frag = document.createDocumentFragment();
        newProducts.forEach((product, index) => {
          const clone = product.cloneNode(true);
          clone.style.opacity='0';
          clone.style.transform='translateY(30px)';
          frag.appendChild(clone);
          requestAnimationFrame(() => {
            setTimeout(()=>{
              clone.style.transition='opacity .6s ease, transform .6s ease';
              clone.style.opacity='1';
              clone.style.transform='translateY(0)';
            }, index * this.config.animationDelay);
          });
        });
        this.grid.appendChild(frag);
        // Ré-init scripts / AOS potentiels
        if (window.AOS && typeof window.AOS.refreshHard === 'function') {
          try { window.AOS.refreshHard(); } catch(e){}
        }
        // Mettre à jour pagination
        this.updatePagination(doc);
        this.repositionTrigger();
        this.dispatchLoadEvent(newProducts.length, currentProductCount);
        const live = document.querySelector('[data-infinite-live]');
        if (live) live.textContent = newProducts.length + ' produits ajoutés';
      } else {
        this.hasNextPage = false;
        this.removeTrigger();
        if (this.pagination) this.pagination.remove();
      }
    } catch (error) {
      console.error('[InfiniteScroll] Erreur chargement:', error);
      if (this.pagination) this.pagination.style.display = 'block';
    } finally {
      this.isLoading = false;
      this.hideLoader();
    }
  }

  updatePagination(doc) {
    const nextLink = doc.querySelector('.pagination-custom__next[href]');
    if (nextLink && !nextLink.classList.contains('is-hidden')) {
      this.nextPageUrl = nextLink.getAttribute('href');
      this.hasNextPage = true;
      this.hidePagination();
    } else {
      this.hasNextPage = false;
      this.nextPageUrl = null;
      if (this.pagination) this.pagination.remove();
    }
  }

  repositionTrigger() {
    if (!this.trigger || !this.hasNextPage) return;
    // S'assurer que le trigger est toujours en dernière position dans la grille
    if (this.trigger.parentElement !== this.grid) {
      this.grid.appendChild(this.trigger);
      return;
    }
    // Le déplacer après le dernier produit
    const lastProduct = this.grid.querySelector('[data-grid-item]:last-of-type');
    if (lastProduct && lastProduct.nextSibling !== this.trigger) {
      this.grid.appendChild(this.trigger);
    }
  }

  removeTrigger() {
    if (this.trigger) {
      if (this.observer) this.observer.unobserve(this.trigger);
      this.trigger.remove();
      this.trigger = null;
    }
  }

  destroy() {
    if (this.observer) this.observer.disconnect();
    this.removeTrigger();
    // Ne ré-affiche pas la pagination (infinite déjà actif) sauf fallback manuel
    // Nettoyage loader state
    this.hideLoader();
  }
}

// Auto-initialisation quand le DOM est prêt
document.addEventListener('DOMContentLoaded', () => {
  initInfiniteScroll();
});

// Fonction d'initialisation
function initInfiniteScroll() {
  // Vérifier si on est sur une page de collection
  if (document.querySelector('[data-products-grid]') && document.querySelector('#CollectionLoop') && document.querySelector('collection-component')) {
    // Détruire l'instance précédente si elle existe
    if (window.infiniteScroll) {
      window.infiniteScroll.destroy();
    }
    window.infiniteScroll = new InfiniteScroll();
    document.dispatchEvent(new CustomEvent('infiniteScroll:initialized'));
  }
}

// Écouter les événements de mutation pour détecter les changements de contenu
// (utilisé quand les filtres rechargent le contenu)
const targetNode = document.querySelector('[data-products-grid]');
if (targetNode) {
  const observer = new MutationObserver((mutations) => {
    let shouldReinit = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        // Vérifier si de nouveaux produits ont été ajoutés par les filtres
        for (let node of mutation.addedNodes) {
          if (node.nodeType === 1 && (node.matches('#CollectionLoop') || node.querySelector('#CollectionLoop'))) {
            shouldReinit = true;
            break;
          }
        }
      }
    });
    
    if (shouldReinit) {
      setTimeout(initInfiniteScroll, 100);
    }
  });
  
  observer.observe(targetNode, {
    childList: true,
    subtree: true
  });
}

// Réinitialiser lors des changements d'historique (navigation back/forward)
window.addEventListener('popstate', () => {
  setTimeout(initInfiniteScroll, 100);
});
